import { PublicationError } from '@streamlain/doc-publication-contract';
import { localAccountKeyError } from '@streamlain/view-config/local-data-seal';

import { share } from '../../connection';
import {
  blobKeyScope,
  decodeLocalJson,
  encodeLocalJson,
} from '../../local-data/payload';
import {
  type BlobRecord,
  BlobStorageBase,
  getBlobRecordSize,
  hasBlobRecordInlineBytes,
  type ListedBlobRecord,
} from '../../storage';
import { IDBConnection, type IDBConnectionOptions } from './db';
import type { IDBEncryptionContext } from './encryption';
import { withIdbRetry } from './retry';
import type { DocStorageSchema } from './schema';

type BlobRow = DocStorageSchema['blobs']['value'];
export class IndexedDBBlobStorage extends BlobStorageBase {
  static readonly identifier = 'IndexedDBBlobStorage';
  override readonly isReadonly = false;
  readonly connection = share(new IDBConnection(this.options));
  constructor(private readonly options: IDBConnectionOptions) {
    super();
  }
  get db() {
    return this.connection.inner.db;
  }
  private get encryption() {
    return this.connection.encryption;
  }
  private async metadata(
    context: IDBEncryptionContext,
    row: BlobRow
  ): Promise<ListedBlobRecord> {
    if (this.encryption.plain) {
      if (
        typeof row.mime !== 'string' ||
        typeof row.size !== 'number' ||
        !(row.createdAt instanceof Date)
      )
        throw localAccountKeyError('CORRUPT');
      return {
        key: row.key,
        mime: row.mime,
        size: row.size,
        createdAt: row.createdAt,
      };
    }
    if (
      !row.storageRevision ||
      row.mime !== undefined ||
      row.size !== undefined ||
      row.createdAt !== undefined
    )
      throw localAccountKeyError('CORRUPT');
    const value = decodeLocalJson(
      await context.open(
        blobKeyScope(row.key),
        blobKeyScope(row.key),
        'blob-metadata',
        row.payload
      )
    );
    if (
      !value ||
      typeof value !== 'object' ||
      !('mime' in value) ||
      typeof value.mime !== 'string' ||
      !('size' in value) ||
      typeof value.size !== 'number' ||
      !Number.isFinite(value.size) ||
      value.size < 0 ||
      !('createdAt' in value) ||
      typeof value.createdAt !== 'string' ||
      !Number.isFinite(Date.parse(value.createdAt))
    )
      throw localAccountKeyError('CORRUPT');
    return {
      key: row.key,
      mime: value.mime,
      size: value.size,
      createdAt: new Date(value.createdAt),
    };
  }
  override async get(key: string) {
    return withIdbRetry(this.connection, async () => {
      const { value, context } = await this.encryption.read(
        ['blobs', 'blobData'],
        false,
        async (tx, context) => {
          const blob = await tx.objectStore('blobs').get(key);
          const data = await tx.objectStore('blobData').get(key);
          if (blob && !blob.deletedAt)
            await context.capture(tx, blobKeyScope(key), false);
          return { blob, data };
        }
      );
      if (!value.blob || value.blob.deletedAt) return null;
      const blob = await this.metadata(context, value.blob);
      let data: Uint8Array | undefined;
      if (value.data) {
        if (this.encryption.plain) {
          if (!(value.data.data instanceof Uint8Array))
            throw localAccountKeyError('CORRUPT');
          data = value.data.data;
        } else {
          if (!value.data.storageRevision || value.data.data !== undefined)
            throw localAccountKeyError('CORRUPT');
          data = await context.open(
            blobKeyScope(key),
            blobKeyScope(key),
            'blob',
            value.data.payload
          );
        }
      }
      await this.encryption.assertRead(context);
      return { ...blob, ...(data ? { data } : {}) };
    });
  }
  override async set(input: BlobRecord) {
    const blob = structuredClone(input);
    if (this.encryption.plain) {
      const metadata = {
        key: blob.key,
        mime: blob.mime,
        size: getBlobRecordSize(blob),
        createdAt: new Date(),
        deletedAt: null,
      };
      return this.encryption.transaction(
        ['blobs', 'blobData'],
        'readwrite',
        async tx => {
          await tx.objectStore('blobs').put(metadata);
          if (hasBlobRecordInlineBytes(blob))
            await tx
              .objectStore('blobData')
              .put({ key: blob.key, data: blob.data! });
          else await tx.objectStore('blobData').delete(blob.key);
        }
      );
    }
    const basis = await this.encryption.transaction(
      ['blobs', 'blobData'],
      'readonly',
      async tx => ({
        blob: await tx.objectStore('blobs').get(blob.key),
        data: await tx.objectStore('blobData').get(blob.key),
      })
    );
    const createdAt = new Date();
    return withIdbRetry(this.connection, () =>
      this.encryption.write(async () => {
        const { context } = await this.encryption.read(
          [],
          true,
          async (tx, context) => {
            await context.capture(tx, blobKeyScope(blob.key), true);
          }
        );
        const meta = {
          mime: blob.mime,
          size: getBlobRecordSize(blob),
          createdAt,
        };
        const keyScopeId = blobKeyScope(blob.key);
        const metadata: BlobRow = this.encryption.plain
          ? { key: blob.key, ...meta, deletedAt: null }
          : {
              key: blob.key,
              deletedAt: null,
              keyScopeId,
              storageRevision: crypto.randomUUID(),
              payload: await context.seal(
                keyScopeId,
                keyScopeId,
                'blob-metadata',
                encodeLocalJson(meta)
              ),
            };
        const bytes = hasBlobRecordInlineBytes(blob)
          ? this.encryption.plain
            ? { key: blob.key, data: blob.data! }
            : {
                key: blob.key,
                keyScopeId,
                storageRevision: crypto.randomUUID(),
                payload: await context.seal(
                  keyScopeId,
                  keyScopeId,
                  'blob',
                  blob.data!
                ),
              }
          : null;
        await this.encryption.commit(
          context,
          ['blobs', 'blobData'],
          async tx => {
            const current = await tx.objectStore('blobs').get(blob.key);
            const data = await tx.objectStore('blobData').get(blob.key);
            if (
              (current?.storageRevision ?? null) !==
                (basis.blob?.storageRevision ?? null) ||
              (data?.storageRevision ?? null) !==
                (basis.data?.storageRevision ?? null)
            )
              throw new PublicationError('LOCAL_CONFLICT');
            await tx.objectStore('blobs').put(metadata);
            if (bytes) await tx.objectStore('blobData').put(bytes);
            else await tx.objectStore('blobData').delete(blob.key);
          }
        );
      })
    );
  }
  override async delete(key: string, permanently: boolean) {
    return withIdbRetry(this.connection, () =>
      this.encryption.transaction(
        ['blobs', 'blobData'],
        'readwrite',
        async tx => {
          if (permanently) {
            await tx.objectStore('blobs').delete(key);
            await tx.objectStore('blobData').delete(key);
          } else {
            const blob = await tx.objectStore('blobs').get(key);
            if (blob)
              await tx.objectStore('blobs').put({
                ...blob,
                deletedAt: new Date(),
                ...(!this.encryption.plain
                  ? { storageRevision: crypto.randomUUID() }
                  : {}),
              });
          }
        }
      )
    );
  }
  override async release() {
    return withIdbRetry(this.connection, () =>
      this.encryption.transaction(
        ['blobs', 'blobData'],
        'readwrite',
        async tx => {
          for await (const item of tx.objectStore('blobs').iterate())
            if (item.value.deletedAt) {
              await item.delete();
              await tx.objectStore('blobData').delete(item.value.key);
            }
        }
      )
    );
  }
  override async list(): Promise<ListedBlobRecord[]> {
    return withIdbRetry(this.connection, async () => {
      const { value, context } = await this.encryption.read(
        ['blobs'],
        false,
        async (tx, context) => {
          const rows: BlobRow[] = [];
          for await (const item of tx.objectStore('blobs').iterate())
            if (!item.value.deletedAt) {
              rows.push(item.value);
              await context.capture(tx, blobKeyScope(item.value.key), false);
            }
          return rows;
        }
      );
      const result = await Promise.all(
        value.map(row => this.metadata(context, row))
      );
      await this.encryption.assertRead(context);
      return result;
    });
  }
}
